import React, { useEffect, useMemo, useState } from 'react';
import {
  UtensilsCrossed, Plus, ArrowLeft, Save, Trash2, RotateCcw, CalendarPlus, CalendarCheck, Tag,
  BookOpen, Users, MapPin, ChevronDown, Lock, PowerOff, ArrowDownUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../../context/LanguageContext';
import { confirm, alertDialog } from '../../components/ui/confirm-dialog';
import { TimePicker } from '../../components/ui/time-picker';
import { todayISO } from '../../lib/utils';
import { useRestaurant } from '../../context/RestaurantContext';
import { locationsService } from '../../services/locationsService';
import apiClient from '../../services/api';
import BuffetStudio from './BuffetStudio';

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring';

const BUFFET_TYPES = [
  ['BREAKFAST', 'Breakfast'],
  ['BRUNCH', 'Brunch'],
  ['LUNCH', 'Lunch'],
  ['HIGH_TEA', 'High Tea'],
  ['DINNER', 'Dinner'],
  ['ALL_DAY', 'All Day'],
];

// 0=Monday … 6=Sunday — same convention as branch operating hours.
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* "19:30" → "7:30 pm". Times are stored 24-hour and read 12-hour: nobody
   working a floor says "the buffet runs till nineteen thirty". Every time this
   module prints goes through here so the studio and the list agree. */
const t12 = (v) => {
  if (!v) return '';
  const [h, m] = String(v).slice(0, 5).split(':').map(Number);
  if (Number.isNaN(h)) return v;
  const ap = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ap}` : `${h12} ${ap}`;
};

/* The date `offset` days from today, as YYYY-MM-DD in LOCAL time — not
   toISOString(), which converts to UTC first and hands back yesterday for
   anyone east of Greenwich late in the evening. */
const isoDay = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/* Whether this buffet is on the counter RIGHT NOW — the one fact about a
   service this screen still needs. Running or ending one is a decision about
   a DATE and lives in Open Buffets; all a template board does with it is lock
   its own card, because guests are being charged against exactly these
   settings and the server refuses the edit anyway. */
const isRunningNow = (b) => (b.day || {}).status === 'RUNNING';

// Rule families → types (mirrors BuffetPriceRule.RULE_TYPES on the backend).
const RULE_FAMILIES = [
  ['Time-based', [
    ['TIME', 'Day / time pricing'],
    ['SEASONAL', 'Seasonal / festival'],
    ['EXPRESS', 'Weekday express'],
    ['MENU_OF_DAY', 'Menu of the day'],
    ['INGREDIENT_INDEX', 'Ingredient-indexed'],
  ]],
  ['Demand & occupancy', [
    ['OCCUPANCY', 'Occupancy-based'],
    ['ADVANCE_BOOKING', 'Advance booking'],
    ['FLASH_DEAL', 'Last-minute flash deal'],
  ]],
  ['Guest-based', [
    ['AGE_TIER', 'Age tiers'],
    ['GROUP_SIZE', 'Group size'],
    ['LOYALTY', 'Membership / loyalty'],
    ['SEGMENT_PROMO', 'Segment promo'],
  ]],
  ['Duration & consumption', [
    ['DURATION', 'Time-limited seating'],
    ['ADDON_TIER', 'Add-on tier / station'],
  ]],
  ['Behavioural / waste control', [
    ['WASTE_SURCHARGE', 'Plate-waste surcharge'],
    ['NO_SHOW_FEE', 'No-show / cancellation fee'],
    ['PREPAID_RATE', 'Prepaid rate'],
  ]],
];
const RULE_TYPE_LABEL = Object.fromEntries(RULE_FAMILIES.flatMap(([, ts]) => ts));
// Types that are fees/policies, never per-head pricing.
const POLICY_TYPES = ['AGE_TIER', 'DURATION', 'ADDON_TIER', 'WASTE_SURCHARGE', 'NO_SHOW_FEE'];

const EMPTY_RULE = () => ({
  rule_type: 'TIME', label: '', days: [], start_time: '', end_time: '',
  date_from: '', date_to: '', price_mode: 'SET',
  adult_price: '', child_price: '', adjust_value: '', config: {},
  priority: 0, is_active: true,
});

const EMPTY_MENU = () => ({
  name: '', description: '', days: [],
  sections: [{ name: '', items: [] }],
  is_active: true,
});

// Quick-add suggestions for guest-type pricing tiers.
const TIER_SUGGESTIONS = ['Veg Adult', 'Non-veg Adult', 'Veg Kids', 'Non-veg Kids', 'Senior'];


function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-800 mb-1.5">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function daysLabel(days) {
  if (!days || days.length === 0 || days.length === 7) return 'Every day';
  return days.map((d) => DAYS[d]).join(', ');
}

function rulePriceLabel(r) {
  if (POLICY_TYPES.includes(r.rule_type) || r.price_mode === 'FEE') {
    return r.adjust_value != null && r.adjust_value !== '' ? `fee Rs ${Number(r.adjust_value).toFixed(0)}` : 'policy';
  }
  if (r.price_mode === 'PERCENT') {
    const v = Number(r.adjust_value);
    return `${v > 0 ? '+' : ''}${v}%`;
  }
  if (r.price_mode === 'AMOUNT') {
    const v = Number(r.adjust_value);
    return `${v > 0 ? '+' : ''}Rs ${Math.abs(v).toFixed(0)}${v < 0 ? ' off' : ''}`;
  }
  return `Rs ${Number(r.adult_price).toFixed(0)}`;
}

/* One dynamic-pricing rule editor card. Fields shown depend on rule_type. */
function RuleCard({ r, i, setRule, setRuleCfg, setRuleType, toggleRuleDay, onRemove }) {
  const cfg = r.config || {};
  const isPolicy = POLICY_TYPES.includes(r.rule_type);
  const showDates = ['SEASONAL', 'FLASH_DEAL', 'INGREDIENT_INDEX', 'MENU_OF_DAY'].includes(r.rule_type);
  const numCfg = (k) => (v) => setRuleCfg(i, k, v === '' ? '' : Number(v));

  return (
    <div className="rounded-md border border-border bg-slate-50/60 p-3">
      {/* Row 1 — what the rule is */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Rule type">
          <select className={inputCls} value={r.rule_type} onChange={(e) => setRuleType(i, e.target.value)}>
            {RULE_FAMILIES.map(([fam, types]) => (
              <optgroup key={fam} label={fam}>
                {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label="Label" required>
          <input className={inputCls} value={r.label} onChange={(e) => setRule(i, 'label', e.target.value)} placeholder={RULE_TYPE_LABEL[r.rule_type]} />
        </Field>
        <Field label="Priority">
          <input className={inputCls} type="number" value={r.priority} onChange={(e) => setRule(i, 'priority', Number(e.target.value) || 0)} />
        </Field>
        <div className="flex items-end justify-end pb-1">
          <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 p-1" aria-label="Remove rule">
            <Trash2 size={15} /> Remove
          </button>
        </div>
      </div>

      {/* Row 2 — pricing */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {!isPolicy && (
          <>
            <Field label="Pricing">
              <select className={inputCls} value={r.price_mode} onChange={(e) => setRule(i, 'price_mode', e.target.value)}>
                <option value="SET">Set per-head price</option>
                <option value="PERCENT">% adjustment (±)</option>
                <option value="AMOUNT">Flat adjustment (±)</option>
              </select>
            </Field>
            {r.price_mode === 'SET' ? (
              <>
                <Field label="Adult price" required>
                  <input className={inputCls} type="number" min="0" value={r.adult_price} onChange={(e) => setRule(i, 'adult_price', e.target.value)} />
                </Field>
                <Field label="Child price">
                  <input className={inputCls} type="number" min="0" value={r.child_price} onChange={(e) => setRule(i, 'child_price', e.target.value)} />
                </Field>
              </>
            ) : (
              <Field label={r.price_mode === 'PERCENT' ? '% change' : 'Rs change'} required
                hint={r.price_mode === 'PERCENT' ? 'e.g. -15 = 15% off, 10 = 10% up' : 'e.g. -100 = Rs 100 off per head'}>
                <input className={inputCls} type="number" value={r.adjust_value} onChange={(e) => setRule(i, 'adjust_value', e.target.value)} />
              </Field>
            )}
          </>
        )}
        {r.rule_type === 'NO_SHOW_FEE' && (
          <Field label="Deposit (Rs)">
            <input className={inputCls} type="number" min="0" value={r.adjust_value} onChange={(e) => setRule(i, 'adjust_value', e.target.value)} />
          </Field>
        )}
        {r.rule_type === 'WASTE_SURCHARGE' && (
          <>
            <Field label="Surcharge (Rs)">
              <input className={inputCls} type="number" min="0" value={r.adjust_value} onChange={(e) => setRule(i, 'adjust_value', e.target.value)} />
            </Field>
            <Field label="Charged per">
              <select className={inputCls} value={cfg.per || 'guest'} onChange={(e) => setRuleCfg(i, 'per', e.target.value)}>
                <option value="guest">Guest</option>
                <option value="table">Table</option>
                <option value="plate">Plate left</option>
              </select>
            </Field>
          </>
        )}
        {r.rule_type === 'ADDON_TIER' && (
          <Field label="Add-on price / head (Rs)">
            <input className={inputCls} type="number" min="0" value={r.adjust_value} onChange={(e) => setRule(i, 'adjust_value', e.target.value)} />
          </Field>
        )}
        {r.rule_type === 'DURATION' && (
          <>
            <Field label="Included minutes">
              <input className={inputCls} type="number" min="0" value={cfg.included_minutes ?? ''} onChange={(e) => numCfg('included_minutes')(e.target.value)} />
            </Field>
            <Field label="Extension fee (Rs)">
              <input className={inputCls} type="number" min="0" value={r.adjust_value} onChange={(e) => setRule(i, 'adjust_value', e.target.value)} />
            </Field>
          </>
        )}
        {r.rule_type === 'AGE_TIER' && (
          <>
            <Field label="Free under age">
              <input className={inputCls} type="number" min="0" value={cfg.free_under_age ?? ''} onChange={(e) => numCfg('free_under_age')(e.target.value)} />
            </Field>
            <Field label="Half price under age">
              <input className={inputCls} type="number" min="0" value={cfg.half_price_under_age ?? ''} onChange={(e) => numCfg('half_price_under_age')(e.target.value)} />
            </Field>
          </>
        )}
      </div>

      {/* Row 3 — type-specific conditions */}
      {(r.rule_type === 'GROUP_SIZE' || r.rule_type === 'ADVANCE_BOOKING'
        || r.rule_type === 'OCCUPANCY' || r.rule_type === 'LOYALTY') && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {r.rule_type === 'GROUP_SIZE' && (
            <>
              <Field label="Min party size">
                <input className={inputCls} type="number" min="2" value={cfg.min_party ?? ''} onChange={(e) => numCfg('min_party')(e.target.value)} />
              </Field>
              <Field label="1 free per N guests">
                <input className={inputCls} type="number" min="0" value={cfg.free_per ?? ''} onChange={(e) => numCfg('free_per')(e.target.value)} />
              </Field>
            </>
          )}
          {r.rule_type === 'ADVANCE_BOOKING' && (
            <Field label="Booked at least (days ahead)">
              <input className={inputCls} type="number" min="1" value={cfg.min_days_ahead ?? ''} onChange={(e) => numCfg('min_days_ahead')(e.target.value)} />
            </Field>
          )}
          {r.rule_type === 'OCCUPANCY' && (
            <>
              <Field label="Occupancy from (%)">
                <input className={inputCls} type="number" min="0" max="100" value={cfg.min_occupancy ?? ''} onChange={(e) => numCfg('min_occupancy')(e.target.value)} />
              </Field>
              <Field label="Occupancy to (%)">
                <input className={inputCls} type="number" min="0" max="100" value={cfg.max_occupancy ?? ''} onChange={(e) => numCfg('max_occupancy')(e.target.value)} />
              </Field>
            </>
          )}
          {r.rule_type === 'LOYALTY' && (
            <Field label="Member tier">
              <input className={inputCls} value={cfg.member_tier || ''} onChange={(e) => setRuleCfg(i, 'member_tier', e.target.value)} placeholder="e.g. Gold" />
            </Field>
          )}
        </div>
      )}

      {/* Row 4 — when it applies */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Time window">
          <div className="flex flex-wrap items-center gap-1">
            <TimePicker value={r.start_time} step={15}
              onChange={(v) => setRule(i, 'start_time', v)} />
            <TimePicker value={r.end_time} step={15}
              onChange={(v) => setRule(i, 'end_time', v)} />
          </div>
        </Field>
        <Field label={showDates ? 'Date range' : 'Date range (optional)'}>
          <div className="flex items-center gap-1">
            {/* A price rule governs what a guest is charged from now on, so a
                new window cannot start in the past — but a rule already
                running keeps its own start date rather than being flagged
                invalid every time something else on it is edited. */}
            <input className={inputCls} type="date" value={r.date_from}
              min={r.date_from && r.date_from < todayISO() ? r.date_from : todayISO()}
              onChange={(e) => setRule(i, 'date_from', e.target.value)} />
            <input className={inputCls} type="date" value={r.date_to}
              min={r.date_to && r.date_to < todayISO()
                ? r.date_to : (r.date_from > todayISO() ? r.date_from : todayISO())}
              onChange={(e) => setRule(i, 'date_to', e.target.value)} />
          </div>
        </Field>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-slate-500 mr-1">Days:</span>
        {DAYS.map((d, di) => (
          <button
            key={d}
            type="button"
            onClick={() => toggleRuleDay(i, di)}
            className={`px-2 py-0.5 rounded-full text-xs border transition ${
              r.days.includes(di)
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-white text-slate-600 border-border hover:bg-slate-50'
            }`}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}

/* One buffet-menu editor card — name, serving days, sections of items. */
function MenuCard({ m, i, setMenu, toggleMenuDay, onRemove }) {
  const sections = m.sections || [];
  const setSections = (next) => setMenu(i, 'sections', next);
  const setSection = (si, f, v) =>
    setSections(sections.map((s, idx) => (idx === si ? { ...s, [f]: v } : s)));

  return (
    <div className="rounded-md border border-border bg-slate-50/60 p-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Menu name" required>
          <input className={inputCls} value={m.name} onChange={(e) => setMenu(i, 'name', e.target.value)} placeholder="e.g. Sunday Special Spread" />
        </Field>
        <Field label="Description">
          <input className={inputCls} value={m.description} onChange={(e) => setMenu(i, 'description', e.target.value)} placeholder="Optional note shown with the menu" />
        </Field>
        <div className="flex items-end justify-end pb-1">
          <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 p-1" aria-label="Remove menu">
            <Trash2 size={15} /> Remove
          </button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-slate-500 mr-1">Served on:</span>
        {DAYS.map((d, di) => (
          <button
            key={d}
            type="button"
            onClick={() => toggleMenuDay(i, di)}
            className={`px-2 py-0.5 rounded-full text-xs border transition ${
              (m.days || []).includes(di)
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-white text-slate-600 border-border hover:bg-slate-50'
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      <div className="mt-3 space-y-2">
        {sections.map((s, si) => (
          <div key={si} className="rounded-md border border-border bg-white p-2.5">
            <div className="flex items-center gap-2 mb-2">
              <input
                className={`${inputCls} md:w-64`}
                value={s.name}
                onChange={(e) => setSection(si, 'name', e.target.value)}
                placeholder="Section, e.g. Starters / Mains / Desserts"
              />
              <button
                type="button"
                onClick={() => setSections(sections.filter((_, idx) => idx !== si))}
                className="ml-auto text-xs text-red-500 hover:text-red-700 p-1"
                aria-label="Remove section"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <textarea
              className={inputCls}
              rows={3}
              value={(s.items || []).join('\n')}
              onChange={(e) => setSection(si, 'items', e.target.value.split('\n'))}
              placeholder={'One item per line, e.g.\nPaneer tikka\nSoup of the day'}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => setSections([...sections, { name: '', items: [] }])}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <Plus size={14} /> Add section
        </button>
      </div>
    </div>
  );
}


export default function BuffetManagement() {
  // useT returns the whole language context; t is one field on it. Called
  // as a bare function it throws on the first label that renders, which
  // unmounts the app and leaves a white page.
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [buffets, setBuffets] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  /* How the board is ordered. The server hands the list back by name (the
     model's own ordering); "newest"/"oldest" re-sort it here by created_at,
     for finding the template somebody just added without reading every row. */
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /* Which detail a card is showing, per buffet id: '' | 'menu' | 'pricing'.
     One at a time, so a board of buffets stays a board rather than becoming
     five stacked reports. */
  const [panel, setPanel] = useState({});
  const clearOverride = async (b) => {
    try {
      const { data } = await apiClient.delete(
        `/users/restaurant/${restaurantId}/buffets/${b.id}/price-override/`);
      if (!data?.success) throw new Error(data?.error || 'Could not clear the price');
      await reload();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  // null = list view; object = editor (id null → creating)
  const [editing, setEditing] = useState(null);
  const [editorTab, setEditorTab] = useState('details');
  // Buffet-studio editor (the redesigned 5-section page). {} → creating,
  // raw buffet dict → editing. The classic tabbed editor remains only as
  // the dynamic-pricing rules editor, reached from the studio.
  const [studio, setStudio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Restaurant menus for the "Menu served" picker on the Details tab.
  const [restMenus, setRestMenus] = useState([]);

  useEffect(() => {
    if (!restaurantId) return;
    locationsService.list(restaurantId).then((locs) => {
      setLocations(locs);
      setLocationId((cur) => cur || String(locs[0]?.id || ''));
    }).catch(() => setLocations([]));
    apiClient.get(`/users/restaurant/${restaurantId}/getAllMenus/`)
      .then(({ data }) => setRestMenus((data?.success && data.data) || []))
      .catch(() => setRestMenus([]));
  }, [restaurantId]);

  const reload = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      /* Today's services, read for one reason only: a buffet on the
         counter right now locks its own card. WHICH day a template runs is
         not this screen's question - that is Open Buffets. */
      const params = { date: isoDay(0) };
      if (locationId) params.location_id = locationId;
      if (showInactive) params.include_inactive = 'true';
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/buffets/`, { params });
      if (!data?.success) throw new Error(data?.error || 'Failed to load buffets');
      setBuffets(data.results || []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [restaurantId, locationId, showInactive]);

  /* The list as the board shows it. A row with no created_at (rows older than
     the column) sorts as the very oldest rather than crashing the comparator. */
  const sortedBuffets = useMemo(() => {
    if (sortBy === 'name') return buffets;
    const ts = (b) => (b.created_at ? new Date(b.created_at).getTime() : 0);
    return [...buffets].sort((a, b) =>
      sortBy === 'newest' ? ts(b) - ts(a) : ts(a) - ts(b));
  }, [buffets, sortBy]);

  const openNew = () => { setStudio({}); setSaveError(''); };
  const openExisting = (b, tab = 'details') => {
    /* A buffet being served is read-only, enforced here rather than only on
       the disabled buttons: the list refreshes on a timer and by date, so a
       card that was "scheduled" when it rendered may be running by the time
       it is clicked. The server refuses the save too. */
    if ((b.day || {}).status === 'RUNNING') {
      alertDialog({
        title: `${b.name} is being served`,
        description: 'A buffet cannot be edited while its service is running — '
          + 'guests are being charged against these settings right now. Close the '
          + 'service first, or edit it on a day it is not running.',
      });
      return;
    }
    // The studio is the default editor; the classic tabbed editor only
    // remains for dynamic-pricing rules.
    if (tab !== 'pricing') { setStudio(b); setSaveError(''); return; }
    setEditorTab(tab);
    setEditing({
      id: b.id,
      name: b.name || '',
      description: b.description || '',
      buffet_type: b.buffet_type || 'LUNCH',
      menu_id: b.menu_id ?? '',
      price_tiers: (b.price_tiers || []).map((t) => ({ label: t.label || '', price: t.price ?? '' })),
      base_adult_price: b.base_adult_price ?? '',
      base_child_price: b.base_child_price ?? '',
      child_age_limit: b.child_age_limit ?? 12,
      start_time: b.start_time || '',
      end_time: b.end_time || '',
      est_food_cost_per_cover: b.est_food_cost_per_cover ?? '',
      fixed_cost_per_day: b.fixed_cost_per_day ?? '',
      is_active: !!b.is_active,
      price_rules: (b.price_rules || []).map((r) => ({
        rule_type: r.rule_type || 'TIME',
        label: r.label || '', days: r.days || [],
        start_time: r.start_time || '', end_time: r.end_time || '',
        date_from: r.date_from || '', date_to: r.date_to || '',
        price_mode: r.price_mode || 'SET',
        adult_price: r.adult_price ?? '', child_price: r.child_price ?? '',
        adjust_value: r.adjust_value ?? '', config: r.config || {},
        priority: r.priority ?? 0, is_active: r.is_active !== false,
      })),
      menus: (b.menus || []).map((m) => ({
        name: m.name || '', description: m.description || '',
        days: m.days || [],
        sections: (m.sections || []).map((s) => ({ name: s.name || '', items: s.items || [] })),
        is_active: m.is_active !== false,
      })),
    });
    setSaveError('');
  };

  const setField = (f, v) => setEditing((p) => ({ ...p, [f]: v }));
  const setRule = (i, f, v) => setEditing((p) => {
    const rules = p.price_rules.map((r, idx) => (idx === i ? { ...r, [f]: v } : r));
    return { ...p, price_rules: rules };
  });
  const setRuleCfg = (i, k, v) => setEditing((p) => {
    const rules = p.price_rules.map((r, idx) =>
      (idx === i ? { ...r, config: { ...(r.config || {}), [k]: v } } : r));
    return { ...p, price_rules: rules };
  });
  const setRuleType = (i, type) => setEditing((p) => {
    const rules = p.price_rules.map((r, idx) => {
      if (idx !== i) return r;
      const isPolicy = POLICY_TYPES.includes(type);
      return {
        ...r,
        rule_type: type,
        price_mode: isPolicy ? 'FEE' : (r.price_mode === 'FEE' ? 'SET' : r.price_mode),
      };
    });
    return { ...p, price_rules: rules };
  });
  const toggleRuleDay = (i, d) => setEditing((p) => {
    const rules = p.price_rules.map((r, idx) => {
      if (idx !== i) return r;
      const days = r.days.includes(d) ? r.days.filter((x) => x !== d) : [...r.days, d].sort();
      return { ...r, days };
    });
    return { ...p, price_rules: rules };
  });
  const setMenu = (i, f, v) => setEditing((p) => {
    const menus = p.menus.map((m, idx) => (idx === i ? { ...m, [f]: v } : m));
    return { ...p, menus };
  });
  const toggleMenuDay = (i, d) => setEditing((p) => {
    const menus = p.menus.map((m, idx) => {
      if (idx !== i) return m;
      const days = (m.days || []).includes(d) ? m.days.filter((x) => x !== d) : [...(m.days || []), d].sort();
      return { ...m, days };
    });
    return { ...p, menus };
  });

  const save = async () => {
    if (!editing.name.trim()) { setSaveError('Name is required'); return; }
    if (editing.base_adult_price === '' || Number(editing.base_adult_price) < 0) {
      setSaveError('Base adult price is required'); return;
    }
    for (const r of editing.price_rules) {
      if (!r.label.trim()) { setSaveError('Every pricing rule needs a label'); return; }
      const isPolicy = POLICY_TYPES.includes(r.rule_type);
      if (!isPolicy && r.price_mode === 'SET' && (r.adult_price === '' || Number(r.adult_price) < 0)) {
        setSaveError(`Rule "${r.label}": adult price is required`); return;
      }
      if (!isPolicy && (r.price_mode === 'PERCENT' || r.price_mode === 'AMOUNT') && r.adjust_value === '') {
        setSaveError(`Rule "${r.label}": adjustment value is required`); return;
      }
    }
    for (const m of editing.menus) {
      if (!m.name.trim()) { setSaveError('Every buffet menu needs a name'); return; }
    }
    for (const t of editing.price_tiers) {
      if (!String(t.label).trim() && t.price === '') continue;
      if (!String(t.label).trim()) { setSaveError('Every guest-type price needs a label'); return; }
      if (t.price === '' || Number(t.price) < 0) {
        setSaveError(`Guest type "${t.label}": price is required`); return;
      }
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...editing,
        location_id: Number(locationId),
        menu_id: editing.menu_id ? Number(editing.menu_id) : null,
        price_tiers: editing.price_tiers.filter((t) => String(t.label).trim()),
      };
      delete payload.id;
      const url = editing.id
        ? `/users/restaurant/${restaurantId}/buffets/${editing.id}/`
        : `/users/restaurant/${restaurantId}/buffets/`;
      const { data } = editing.id
        ? await apiClient.put(url, payload)
        : await apiClient.post(url, payload);
      if (!data?.success) throw new Error(data?.error || 'Failed to save');
      await reload();
      setEditing(null);
    } catch (e) {
      setSaveError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (b) => {
    // The button is already disabled on a running service; this catches the
    // one the list is stale about — someone else started it a moment ago.
    if (isRunningNow(b)) {
      await alertDialog({
        title: 'It is being served right now',
        description: `"${b.name}" has guests at the counter. Close the service first, `
          + 'then it can be deactivated.',
        tone: 'destructive',
      });
      return;
    }
    /* Not a destructive tone, because nothing is destroyed: the template
       keeps its spread, its prices and its whole history, and Reactivate
       brings it straight back. What it DOES lose is its place in the list of
       templates you can open on a date — which is the consequence worth
       stating, since that is the one someone will notice tomorrow. */
    if (!(await confirm({
      title: `Deactivate "${b.name}"?`,
      description: 'It stops being offered when you open a buffet on a date. '
        + 'Nothing is deleted — the template, its menu, its prices and its past '
        + 'services are all kept, and Reactivate puts it back.',
      confirmText: 'Deactivate',
    }))) return;
    try {
      // The exact mirror of reactivate() below. A buffet template has no
      // delete — only this flag.
      await apiClient.put(`/users/restaurant/${restaurantId}/buffets/${b.id}/`,
        { is_active: false });
      await reload();
    } catch (e) {
      // The server refuses a running service too (409) — its wording names
      // the buffet and what to do, so prefer it over a generic failure.
      await alertDialog({
        title: 'Could not deactivate',
        description: e?.response?.data?.error || 'Failed to deactivate',
        tone: 'destructive',
      });
      await reload();
    }
  };

  /* Deleting is NOT the same button as Deactivate, and the difference is the
     whole point of having both: deactivate parks a template that has a past,
     delete removes one that never had one. The server decides which is which -
     it refuses outright when covers were sold, a service ran, or a booking
     points at the template - so the button asks rather than guesses, and shows
     whatever the refusal says. */
  const removeBuffet = async (b) => {
    if (isRunningNow(b)) {
      await alertDialog({
        title: t('buffet.del.runningTitle'),
        description: t('buffet.del.runningBody', { name: b.name }),
        tone: 'destructive',
      });
      return;
    }
    const opened = b.schedule?.opened || 0;
    if (!(await confirm({
      title: t('buffet.del.confirmTitle', { name: b.name }),
      description: opened
        ? t('buffet.del.confirmWithDates', { n: opened })
        : t('buffet.del.confirmBody'),
      confirmText: t('buffet.del.confirmCta'),
      tone: 'destructive',
    }))) return;
    try {
      await apiClient.delete(
        `/users/restaurant/${restaurantId}/buffets/${b.id}/?purge=1`);
      await reload();
    } catch (e) {
      // The server's refusal names what is standing in the way and what to do
      // instead; a generic "failed" would throw exactly that away.
      await alertDialog({
        title: t('buffet.del.failedTitle'),
        description: e?.response?.data?.error || t('buffet.del.failedBody'),
        tone: 'destructive',
      });
      await reload();
    }
  };

  const reactivate = async (b) => {
    try {
      await apiClient.put(`/users/restaurant/${restaurantId}/buffets/${b.id}/`, { is_active: true });
    } catch (e) { /* surfaced by reload showing unchanged state */ }
    await reload();
  };

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          No restaurant selected. Pick a restaurant first to manage buffets.
        </div>
      </div>
    );
  }

  /* ── Buffet Studio (redesigned editor) ──────────────────────────────── */
  if (studio) {
    return (
      <BuffetStudio
        restaurantId={restaurantId}
        locationId={locationId}
        buffet={studio.id ? studio : null}
        onBack={() => setStudio(null)}
        onSaved={() => { setStudio(null); reload(); }}
        onOpenRules={studio.id ? () => { const b = studio; setStudio(null); openExisting(b, 'pricing'); } : null}
      />
    );
  }

  /* ── Classic editor (dynamic-pricing rules only) ────────────────────── */
  if (editing) {
    return (
      <div className="p-6 w-full">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft size={16} /> Back to templates
        </button>
        <h1 className="font-display text-2xl font-semibold mb-6">
          {editing.id ? `Edit: ${editing.name || 'Buffet'}` : 'New Buffet'}
        </h1>

        {saveError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{saveError}</div>
        )}

        {/* Editor tabs — Details / Buffet Menus / Dynamic Pricing */}
        <div className="mb-4 inline-flex rounded-lg border border-border bg-white p-1 gap-1">
          {[['details', 'Details'], ['menus', 'Buffet Menus'], ['pricing', 'Dynamic Pricing']].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setEditorTab(k)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                editorTab === k
                  ? 'bg-primary text-primary-foreground'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-border bg-white p-5 space-y-4">
          {editorTab === 'details' && (<>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Buffet name" required>
              <input className={inputCls} value={editing.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Sunday Brunch Buffet" />
            </Field>
            <Field label="Type">
              <select className={inputCls} value={editing.buffet_type} onChange={(e) => setField('buffet_type', e.target.value)}>
                {BUFFET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Menu served">
              <select className={inputCls} value={editing.menu_id || ''} onChange={(e) => setField('menu_id', e.target.value)}>
                <option value="">— No linked menu —</option>
                {restMenus
                  .filter((m) => m.menu_type === 'BUFFET'
                    // Keep a stale à-la-carte link visible so it isn't silently dropped on save.
                    || String(m.id) === String(editing.menu_id || ''))
                  .map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea className={inputCls} rows={2} value={editing.description} onChange={(e) => setField('description', e.target.value)} placeholder="What's on the spread…" />
          </Field>

          {/* ── Pricing by guest type ─────────────────────────────────── */}
          <div className="pt-2 border-t border-border">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-1">
              <Users size={14} className="text-primary" /> Pricing by guest type
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Adult price" required>
                <input className={inputCls} type="number" min="0" value={editing.base_adult_price} onChange={(e) => setField('base_adult_price', e.target.value)} />
              </Field>
              <Field label="Child price">
                <input className={inputCls} type="number" min="0" value={editing.base_child_price} onChange={(e) => setField('base_child_price', e.target.value)} />
              </Field>
              <Field label="Child age up to">
                <input className={inputCls} type="number" min="0" value={editing.child_age_limit} onChange={(e) => setField('child_age_limit', e.target.value)} />
              </Field>
              <Field label="Serving window">
                <div className="flex flex-wrap items-center gap-1.5">
                  <TimePicker value={editing.start_time} step={15}
                    onChange={(v) => setField('start_time', v)} />
                  <span className="text-xs text-slate-400">to</span>
                  <TimePicker value={editing.end_time} step={15}
                    onChange={(v) => setField('end_time', v)} />
                </div>
              </Field>
            </div>

            <div className="mt-3 space-y-2">
              {editing.price_tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${inputCls} md:w-64`}
                    value={t.label}
                    onChange={(e) => setField('price_tiers', editing.price_tiers.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
                    placeholder="Guest type, e.g. Veg Adult"
                    list="tier-suggestions"
                  />
                  <input
                    className={`${inputCls} md:w-40`}
                    type="number" min="0"
                    value={t.price}
                    onChange={(e) => setField('price_tiers', editing.price_tiers.map((x, idx) => (idx === i ? { ...x, price: e.target.value } : x)))}
                    placeholder="Price / head"
                  />
                  <button type="button"
                    onClick={() => setField('price_tiers', editing.price_tiers.filter((_, idx) => idx !== i))}
                    className="text-red-500 hover:text-red-700 p-1" aria-label="Remove guest type">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <datalist id="tier-suggestions">
                {TIER_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
              </datalist>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button"
                  onClick={() => setField('price_tiers', [...editing.price_tiers, { label: '', price: '' }])}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                  <Plus size={14} /> Add guest type
                </button>
                {TIER_SUGGESTIONS.filter((s) => !editing.price_tiers.some((t) => t.label === s)).map((s) => (
                  <button key={s} type="button"
                    onClick={() => setField('price_tiers', [...editing.price_tiers, { label: s, price: '' }])}
                    className="px-2 py-0.5 rounded-full text-xs border bg-white text-slate-600 border-border hover:bg-slate-50">
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Est. food cost / cover">
              <input className={inputCls} type="number" min="0" value={editing.est_food_cost_per_cover} onChange={(e) => setField('est_food_cost_per_cover', e.target.value)} />
            </Field>
            <Field label="Fixed cost / day">
              <input className={inputCls} type="number" min="0" value={editing.fixed_cost_per_day} onChange={(e) => setField('fixed_cost_per_day', e.target.value)} />
            </Field>
          </div>
          </>)}

          {/* ── Buffet menus (what's on the counter) ──────────────────── */}
          {editorTab === 'menus' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <BookOpen size={14} className="text-primary" /> Buffet menus
              </h3>
              <button
                type="button"
                onClick={() => setField('menus', [...editing.menus, EMPTY_MENU()])}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <Plus size={14} /> Add menu
              </button>
            </div>
            {editing.menus.length === 0 && (
              <p className="text-sm text-slate-400 italic">No menus yet — add one to describe what's on the spread.</p>
            )}
            <div className="space-y-3">
              {editing.menus.map((m, i) => (
                <MenuCard
                  key={i}
                  m={m}
                  i={i}
                  setMenu={setMenu}
                  toggleMenuDay={toggleMenuDay}
                  onRemove={() => setField('menus', editing.menus.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          </div>
          )}

          {/* ── Dynamic pricing rules ─────────────────────────────────── */}
          {editorTab === 'pricing' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Tag size={14} className="text-primary" /> Dynamic pricing rules
              </h3>
              <button
                type="button"
                onClick={() => setField('price_rules', [...editing.price_rules, EMPTY_RULE()])}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <Plus size={14} /> Add rule
              </button>
            </div>
            {editing.price_rules.length === 0 && (
              <p className="text-sm text-slate-400 italic">No rules — the base price applies at all times.</p>
            )}
            <div className="space-y-3">
              {editing.price_rules.map((r, i) => (
                <RuleCard
                  key={i}
                  r={r}
                  i={i}
                  setRule={setRule}
                  setRuleCfg={setRuleCfg}
                  setRuleType={setRuleType}
                  toggleRuleDay={toggleRuleDay}
                  onRemove={() => setField('price_rules', editing.price_rules.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-border text-sm text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
              <Save size={15} /> {saving ? 'Saving…' : 'Save buffet'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── The Buffet List board ──────────────────────────────────────────── */
  return (
    <div className="p-6 w-full">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          {/* This list IS the buffets — name, spread, quantities and prices.
              Which dates one runs on is a separate job with its own module,
              which the line below points at so the two are never confused. */}
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
            Buffet List
          </h1>
          {/* Where the other half is. Running a service — start it, work the
              counter, end the day — belongs to a DATE, so this board points at
              it rather than offering buttons a template has no day to answer. */}
          <p className="mt-1 text-sm text-muted-foreground">
            Define a buffet here — what it is, what goes on the counter, what it costs.
            Putting it on a date, and running the service that day, is{' '}
            <Link to="/buffets/open" className="font-medium text-primary hover:underline">
              Open Buffets
            </Link>.
          </p>
        </div>
        {/* Toolbar: the two filters read as a pair — same height, same frame —
            with the primary action set apart so it is not taken for a third
            filter. `shrink-0` and `whitespace-nowrap` keep the row on one line
            instead of letting the heading squeeze the labels into two. */}
        <div className="flex items-center gap-2 shrink-0">
          {locations.length > 1 ? (
            <div className="relative flex h-9 w-52 items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring/30">
              <MapPin className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <select
                aria-label="Branch"
                className="h-full w-full cursor-pointer appearance-none bg-transparent pl-8 pr-8 text-sm text-foreground focus:outline-none"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-muted-foreground" />
            </div>
          ) : locations.length === 1 && (
            /* Nothing to choose between — say which branch this is rather than
               offering a dropdown with one option in it. */
            <span className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground whitespace-nowrap">
              <MapPin className="h-3.5 w-3.5" />
              <span className="font-medium text-foreground">{locations[0].name}</span>
            </span>
          )}
          {/* Same frame as the branch picker so the toolbar reads as one row
              of filters. Sorting is a way of READING the board, not changing
              it — the numbered badges renumber with it. */}
          <div className="relative flex h-9 items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring/30"
            title="Order the board — by name, or by when each template was created">
            <ArrowDownUp className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <select
              aria-label="Sort templates"
              className="h-full w-full cursor-pointer appearance-none bg-transparent pl-8 pr-8 text-sm text-foreground focus:outline-none"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Name A–Z</option>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <label className="inline-flex h-9 cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/40">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
            />
            Show drafts
          </label>
          {(
            <button
              onClick={openNew}
              className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Plus size={16} /> Create New Buffet
            </button>
          )}
        </div>
      </header>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading buffets…</p>
      ) : buffets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-slate-500">
          <UtensilsCrossed className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          No buffets for this branch yet. <button onClick={openNew} className="text-primary hover:underline">Create the first one</button>.
        </div>
      ) : (
        <div className="space-y-3">
          {sortedBuffets.map((b, i) => {
            // Serving now: the buffet is on the counter and guests are being
            // charged against exactly these settings, so it is read-only until
            // the service closes. Changing the spread or the per-head price
            // mid-service would re-price a room that is already eating.
            const live = isRunningNow(b);
            return (
            <div key={b.id} className={`rounded-lg border border-border bg-white p-4 ${
              b.is_active ? '' : 'opacity-60'}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {/* Position on the board — what staff point at across a
                    counter ("number two is running low") rather than reading
                    a name back at each other. */}
                <span className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold tabular-nums text-slate-500">
                  {i + 1}
                </span>
                {/* The name is the way in: reading a row and wanting its
                    details is one motion, so the name opens the same door as
                    Edit — the studio with everything seeable and settable —
                    including its being-served guard (a live buffet explains
                    itself instead of opening a form the server would refuse).
                    In pricing view it opens the rules editor, matching the
                    row's own button. */}
                <button
                  type="button"
                  onClick={() => openExisting(b, 'details')}
                  title={live
                    ? `${b.name} is being served — its settings are locked until the service closes`
                    : `Open ${b.name} — see and change its details`}
                  className="font-semibold text-sm text-primary hover:underline text-left"
                >
                  {b.name}
                </button>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                  {(BUFFET_TYPES.find(([v]) => v === b.buffet_type) || [])[1] || b.buffet_type}
                </span>
                {b.created_at && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400"
                    title={`Created ${new Date(b.created_at).toLocaleString()}`}>
                    <CalendarPlus size={12} />
                    {new Date(b.created_at).toLocaleDateString([], {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </span>
                )}
                {/* What the template has actually DONE: the dates it has been
                    opened on, and how many of those services ran. A template
                    that has never reached the calendar says so plainly — that
                    is the row a board review is looking for. */}
                {b.schedule && (
                  b.schedule.opened > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500"
                      title={`Opened on ${b.schedule.opened} date${b.schedule.opened === 1 ? '' : 's'} in Open Buffets — ${
                        b.schedule.ran} of those services ran (started and served)`}>
                      <CalendarCheck size={12} className="text-emerald-600" />
                      <span className="tabular-nums">
                        {b.schedule.opened} date{b.schedule.opened === 1 ? '' : 's'} · {b.schedule.ran} ran
                      </span>
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400"
                      title="This template has not been put on any date yet — that happens in Open Buffets">
                      never opened on a date
                    </span>
                  )
                )}
                {/* One flag, one word: a buffet that is not live is a DRAFT
                    here and behind the "Show drafts" filter that reveals it —
                    calling it "inactive" in one place and "draft" in the
                    other described the same row two ways. */}
                {!b.is_active && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500"
                    title="Not live — invisible to guests and the till until it is saved as active">
                    DRAFT
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  {b.is_active ? (
                    <>
                      {/* Locked while the buffet is being served — see `live`
                          above. Greyed with the reason rather than hidden, so
                          it doesn't read as a missing button. */}
                      <button
                        onClick={() => openExisting(b)}
                        disabled={live}
                        title={live
                          ? 'Being served right now — close the service before editing it'
                          : 'Edit this buffet'}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-border disabled:text-slate-300 disabled:hover:bg-transparent"
                      >
                        {live && <Lock size={12} />} Edit
                      </button>
                      {/* Named rather than an X, and not red: a template is
                          never deleted, so the board must not offer anything
                          that reads as deletion. A buffet being served cannot
                          even be deactivated — the till would stop selling
                          covers for a room that is still eating — so it greys
                          with the reason rather than hiding. */}
                      <button
                        onClick={() => deactivate(b)}
                        disabled={live}
                        title={live
                          ? 'Being served right now — close the service before deactivating it'
                          : 'Stop offering this template — it is kept, and can be reactivated'}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                      >
                        <PowerOff size={13} /> Deactivate
                      </button>
                    </>
                  ) : (
                    <button onClick={() => reactivate(b)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-xs text-slate-700 hover:bg-slate-50">
                      <RotateCcw size={13} /> Reactivate
                    </button>
                  )}
                  {/* Red, and last: the one action on this row that does not
                      come back. Offered on drafts as well as live templates,
                      because a list full of half-finished tries is exactly
                      what somebody wants to clear, and greyed mid-service for
                      the same reason every other action here is. */}
                  <button
                    onClick={() => removeBuffet(b)}
                    disabled={live}
                    title={live
                      ? t('buffet.del.titleLive')
                      : t('buffet.del.title')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-border disabled:text-slate-300 disabled:hover:bg-transparent"
                  >
                    <Trash2 size={13} /> {t('buffet.del.button')}
                  </button>
                </span>
              </div>

              {/* The card used to print the price, the base price, the menu
                  name, every price tier and every spread as chips — five rows
                  of detail on every buffet, which is a wall to read through
                  when what you came for is "which one is running". They are
                  all still here, one tap away, behind three plain options. */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {[
                  ['menu', BookOpen, 'View menu'],
                  ['pricing', Tag, 'Show pricing'],
                ].map(([key, Icon, label]) => {
                  const on = (panel[b.id] || '') === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPanel((p) => ({ ...p, [b.id]: on ? '' : key }))}
                      aria-expanded={on}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition ${
                        on ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-slate-600 hover:bg-slate-50'}`}
                    >
                      <Icon size={13} /> {label}
                    </button>
                  );
                })}
                {/* No capacity here. How full the hall got, and who came, is a
                    fact about a DATE — it lives on that date's card in Open
                    Buffets. A template has no covers of its own to show. */}
              </div>

              {panel[b.id] === 'pricing' && (() => {
                const override = b.config?.price_override;
                const tax = b.price_now?.tax;
                const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                }) : '');
                return (
                <div className="mt-3 rounded-lg border border-border bg-slate-50/60 p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    {override && (
                      <span className="rounded-md border border-violet-300 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-800"
                        title={override.note || 'Set by hand — beats every rule until cleared'}>
                        Hand-set since {when(override.set_at)}{override.by ? ` · ${override.by}` : ''}
                      </span>
                    )}
                    {String(b.config?.pricing_mode || '').toUpperCase() === 'FIXED' && (
                      <span className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800"
                        title="Charged at all times — pricing rules are kept but not applied while fixed pricing is on">
                        Fixed price
                      </span>
                    )}
                    <span className="rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-emerald-800 font-medium">
                      Now: Rs {Number(b.price_now?.adult ?? b.base_adult_price).toFixed(2)} / adult
                      {b.price_now?.child != null && <> · Rs {Number(b.price_now.child).toFixed(2)} / child</>}
                      {b.price_now?.rule_label && <span className="font-normal text-emerald-600"> ({b.price_now.rule_label})</span>}
                    </span>
                    {/* What the guest hands over, whichever way the buffet
                        types its prices. On an inclusive ticket that IS the
                        price above, so the chip says what the tax is doing
                        rather than repeating the same number as news. */}
                    {tax?.adult_final != null && (
                      <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700"
                        title={(tax.labels || []).map((t) => `${t.name} ${t.rate_type === 'FLAT' ? `Rs ${t.rate}` : `${t.rate}%`}`).join(' + ')}>
                        {tax.inclusive
                          ? <>tax included · you keep Rs {Number(tax.adult_net).toFixed(2)}</>
                          : <>guest pays Rs {Number(tax.adult_final).toFixed(2)}
                            {tax.child_final != null && <> / child Rs {Number(tax.child_final).toFixed(2)}</>}</>}
                      </span>
                    )}
                    <span className="text-xs text-slate-500">
                      Base Rs {Number(b.base_adult_price).toFixed(2)}
                      {b.base_child_price != null && <> / child Rs {Number(b.base_child_price).toFixed(2)} (up to {b.child_age_limit} yrs)</>}
                    </span>
                  </div>

                  {/* Setting a price by hand belongs to a service, not to the
                      template that defines one, and the sales history is what
                      past services took. What stays is the way OUT of an
                      override already in force: nothing else in the app can
                      clear one, so without this it would charge on unseen. */}
                  {override && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => clearOverride(b)}
                        className="rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        title="Charges go back to the buffet's own pricing (fixed price or the dynamic rules) immediately"
                      >
                        Back to normal pricing
                      </button>
                    </div>
                  )}

                  {(b.price_tiers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {b.price_tiers.map((t) => (
                        <span key={t.label} className="px-2 py-0.5 rounded-full text-[11px] border bg-white border-slate-200 text-slate-700">
                          {t.label}: Rs {Number(t.price).toFixed(0)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                );
              })()}

              {panel[b.id] === 'menu' && (
                <div className="mt-3 rounded-lg border border-border bg-slate-50/60 p-3">
                  {b.menu_name && (
                    <p className="text-xs text-slate-500 mb-2">
                      Served from <span className="font-medium text-slate-700">{b.menu_name}</span>
                    </p>
                  )}
                  {(b.menus || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No spread listed for this buffet yet — add one in the studio's Buffet menu step.
                    </p>
                  ) : (
                    /* The whole spread, course by course, rather than the
                       item COUNT the chip used to show — "11 items" is not
                       something anyone can act on. */
                    <div className="space-y-3">
                      {b.menus.map((m) => (
                        <div key={m.id}>
                          <div className="text-xs font-semibold text-slate-700">
                            {m.name} <span className="font-normal text-slate-500">· {daysLabel(m.days)}</span>
                          </div>
                          {(m.sections || []).map((sec, si) => (
                            <div key={si} className="mt-1.5 ml-1">
                              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                                {sec.name}
                              </div>
                              <div className="text-sm text-slate-700">
                                {(sec.items || []).join(' · ') || '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(b.price_rules || []).length > 0 && (() => {
                /* A fixed-price buffet keeps its rules on file but none of
                   them is charged — say so and grey them rather than showing
                   live-looking amber chips over a price that never moves. */
                const fixed = String(b.config?.pricing_mode || '').toUpperCase() === 'FIXED';
                return (
                  <div className={`mt-2 flex flex-wrap items-center gap-1.5 ${fixed ? 'opacity-60' : ''}`}>
                    {fixed && (
                      <span className="text-[11px] font-medium text-sky-700">
                        Fixed pricing — rules kept, not applied:
                      </span>
                    )}
                    {b.price_rules.map((r) => (
                      <span key={r.id} className={`px-2 py-0.5 rounded-full text-[11px] border ${r.is_active && !fixed ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-400'} ${!r.is_active ? 'line-through' : ''}`}>
                        {RULE_TYPE_LABEL[r.rule_type] || r.rule_type} · {r.label}: {rulePriceLabel(r)} · {daysLabel(r.days)}
                        {(r.start_time || r.end_time) ? ` · ${t12(r.start_time) || '…'} – ${t12(r.end_time) || '…'}` : ''}
                        {(r.date_from || r.date_to) ? ` · ${r.date_from || '…'}→${r.date_to || '…'}` : ''}
                      </span>
                    ))}
                  </div>
                );
              })()}
              {b.description && <p className="mt-2 text-xs text-slate-500">{b.description}</p>}
            </div>
            );
          })}
        </div>
      )}


    </div>
  );
}
